import React, { useEffect, useRef, useState } from "react";

import { Tooltip } from "@mui/material";
import ShipeeziNewGrid, { Column, GeneralActions } from "Components/AppGrid/shipeezi-new-grid";
import { useTranslation } from "react-i18next";

import Button, { Color, Colors } from "../../../../../../components/Form/Button";
import ConfirmChangeStatus from "../../../../../../components/Generic/ConfirmChangeStatusModal";
import { NotificationToast } from "../../../../../../components/Generic/Toast/NotificationToast";
import { useAppState } from "../../../../../../context/StateContext";
import {
  AppEvents,
  Checklist as ChecklistProps,
  JobsType,
  TasksType,
} from "../../../../../../models/checklist";
import { Row } from "../../../../../../models/shipeezi.table";
import { Status } from "../../../../../../util/enums";
import { onSaveItemSelected } from "../../../../../../util/onSaveItemSelected";
import ChecklistModal from "./ChecklistModal";
import useStyles from "./styles";
import useActions from "./useActions";
import { isShowAction } from "Util/permissions";
import {
  MoreOptions,
  MoreOptionsComponentHandles,
} from "Components/MoreOptionsComponent/MoreOptions";
import { VisibleMoreOptionsComponent } from "./components/VisibleMoreOptions";
import { RefreshCircle, Eye, Edit, Add } from "iconsax-react";
import { colors } from "@zapalacorp/tokens";
import BadgeStatus from "Components/Generic/BadgeStatus";
import { ChecklistProvider } from "./ChecklistModal/context";
import { parseQuestionsWithLogic } from "./utils";
import { Branch } from "Models/branch";

// TODO Felipe Added fixed this triggers
export const CHECKLIST_TRIGGERS = [
  { value: "beforeAcceptTask", label: "beforeAcceptTask" },
  { value: "beforeStartTask", label: "beforeStartTask" },
  { value: "beforeContinueTask", label: "beforeContinueTask" },
  { value: "beforeFinishTask", label: "beforeFinishTask" },
  { value: "manual", label: "manual" },
];

type Props = {
  permission?: {
    edit: boolean;
    create: boolean;
    changeStatus: boolean;
  };
};

const Checklist = (props: Props) => {
  const { permission } = props;
  const classes = useStyles();
  const { currentUser } = useAppState();
  const { t: translate } = useTranslation();
  const {
    checklists,
    hasErrors,
    total,
    page,
    pageSize,
    isLoading,
    setHasErrors,
    setPageSize,
    setCurrentPage,
    handleSearch,
    handleFilter,
    handleGetDefaultFilters,
    handleSort,
    updateChecklist,
    createChecklist,
  } = useActions();

  const [openModal, setOpenModal] = useState<boolean>(false);
  const [isView, setIsView] = useState<boolean>(false);
  const [selectedChecklist, setSelectedChecklist] = useState<ChecklistProps>();

  const [rows, setRows] = useState<Row[]>([]);
  const [openConfirmation, setOpenConfirmation] = useState<boolean>(false);
  const [clearSelection, setClearSelection] = useState<boolean>(false);

  const editChecklist = (index: number) => {
    if (hasErrors !== undefined) setHasErrors(undefined);
    let checklistList: ChecklistProps[] = [];

    try {
      checklistList = JSON.parse(JSON.stringify(checklists));
    } catch {
      checklistList = [...checklists];
    }
    const checklistToUpdate = checklistList.find((checklist) => checklist.id === index);

    if (checklistToUpdate) {
      setSelectedChecklist({
        ...checklistToUpdate,
        questions: parseQuestionsWithLogic(checklistToUpdate.questions || []),
      });
      setIsView(false);
      setOpenModal(true);
    }
  };

  const viewChecklist = (index: number) => {
    const checklistToView = checklists.find((checklist) => checklist.id === index);

    return (
      <Tooltip title={translate("view") || ""} placement="top">
        <div>
          <Eye
            size={18}
            color={colors.gray600}
            className={classes.button}
            onClick={() => {
              if (hasErrors !== undefined) setHasErrors(undefined);
              if (checklistToView) {
                setIsView(true);
                setSelectedChecklist({
                  ...checklistToView,
                  questions: parseQuestionsWithLogic(checklistToView.questions || []),
                });
                setOpenModal(true);
              }
            }}
          />
        </div>
      </Tooltip>
    );
  };

  const editChecklistButtonAndAction = (index: number) => {
    if (permission && isShowAction(currentUser, permission.edit)) {
      return (
        <Tooltip title={translate("edit") || ""} placement="top">
          <div>
            <Edit
              size={18}
              color={colors.gray600}
              className={classes.button}
              onClick={() => editChecklist(index)}
            />
          </div>
        </Tooltip>
      );
    }

    return null;
  };

  const changeStatusChecklistButtonAndAction = (index: number) => {
    if (permission && isShowAction(currentUser, permission.edit)) {
      return (
        <Tooltip title={translate("changeStatus") || ""} placement="top">
          <div>
            <RefreshCircle
              size={18}
              color={colors.gray600}
              className={classes.button}
              onClick={() => changeStatusChecklist(index)}
            />
          </div>
        </Tooltip>
      );
    }

    return null;
  };

  const changeStatusChecklist = (index: number) => {
    const checklistToUpdate = checklists.find((checklist) => checklist.id === index);

    if (checklistToUpdate) {
      updateChecklist({
        id: Number(index),
        checklist: {
          ...checklistToUpdate,
          status: checklistToUpdate.status === Status.ACTIVE ? Status.INACTIVE : Status.ACTIVE,
        },
      });
    }
  };

  const renderJobsTypesName = (jobsType: JobsType[]) => {
    if (jobsType && jobsType.length > 0) {
      const JobTypesNames = jobsType.map((jobType) => jobType.name).filter(Boolean);
      const firstFourJobTypesNames = JobTypesNames.slice(0, 4).join(" / ") ?? "";

      return (
        <MoreOptions
          title={translate("moreJobTypes")}
          manyOptionsToShow={4}
          data={JobTypesNames}
          returnLabel={(jobTypeName) => jobTypeName}
          asComponent={(label) => <span className={classes.label}>{label}</span>}
          visibleComponent={() => (
            <VisibleMoreOptionsComponent optionsNames={firstFourJobTypesNames} />
          )}
        />
      );
    }
  };

  const renderTasksTypesName = (tasksType: TasksType[]) => {
    if (tasksType && tasksType.length > 0) {
      const taskTypesNames = tasksType.map((taskType) => taskType.name).filter(Boolean);
      const firstFourJobTypesNames = taskTypesNames.slice(0, 4).join(" / ") ?? "";

      return (
        <MoreOptions
          title={translate("moreTaskTypes")}
          manyOptionsToShow={4}
          data={taskTypesNames}
          returnLabel={(taskTypeName) => taskTypeName}
          asComponent={(label) => <span className={classes.label}>{label}</span>}
          visibleComponent={() => (
            <VisibleMoreOptionsComponent optionsNames={firstFourJobTypesNames} />
          )}
        />
      );
    }
  };

  const renderAppEventsName = (appEvents: AppEvents[]) => {
    if (appEvents && appEvents.length > 0) {
      const appTriggersNames = appEvents.map((appTrigger) => appTrigger.type);
      const checklistsTriggersNames = appTriggersNames
        .map((trigger) => {
          const chkTrigger = CHECKLIST_TRIGGERS.find((chkTrigger) => chkTrigger.value === trigger);

          if (chkTrigger) return translate(chkTrigger.label);

          return null;
        })
        .filter(Boolean);
      const firstFourAppTriggersNames = checklistsTriggersNames.slice(0, 4).join(" / ") ?? "";

      return (
        <MoreOptions
          title={translate("moreTriggers")}
          manyOptionsToShow={4}
          data={checklistsTriggersNames}
          returnLabel={(checklistTriggerName) => checklistTriggerName}
          asComponent={(label) => <span className={classes.label}>{label}</span>}
          visibleComponent={() => (
            <VisibleMoreOptionsComponent optionsNames={firstFourAppTriggersNames} />
          )}
        />
      );
    }
  };

  const renderButtonToChangeStatus = () => {
    if (rows.length > 0) {
      return (
        <Button
          tooltip={translate("changeStatus")}
          className={classes.changeStatusButton}
          customColor={Color.INFO}
          onClick={() => setOpenConfirmation(true)}
          data-cy="changeStatus"
        >
          <RefreshCircle color={Colors.INFO.text.color} size={16} className="mr-2" />
          {translate("changeStatus")}
        </Button>
      );
    }
  };

  const getBranchesReferences = (branches?: Branch[]) => {
    if (!branches || branches.length === 0) {
      return [];
    }

    const branchesSorted = [...branches]
      .filter((b) => !!b?.name?.trim())
      .sort((a, b) => (a.name ?? "").localeCompare(b.name ?? "", "pt-BR"));

    const branchesNames = branchesSorted.map((branch) => branch.name).filter(Boolean);
    const firstFourBranchesNames = branchesNames.slice(0, 4).join(" / ") ?? "";

    return (
      <MoreOptions
        title={translate("moreBranches")}
        manyOptionsToShow={4}
        data={branchesSorted}
        returnLabel={(branch: Branch) => branch?.name ?? ""}
        asComponent={(label) => <span className={classes.label}>{label}</span>}
        visibleComponent={() => (
          <VisibleMoreOptionsComponent optionsNames={firstFourBranchesNames} />
        )}
      />
    );
  };

  const handleSaveChecklistSelected = (rowsArr: number[]) => {
    const rowsSelected = onSaveItemSelected(rowsArr, rows, checklists);

    setRows([...rowsSelected]);
  };

  const tableColumns: Column[] = [
    { name: "code", title: translate("code") },
    {
      name: "jobsType",
      title: translate("jobTypes"),
      hideSort: true,
      hideSearch: true,
      formatComponent: renderJobsTypesName,
    },
    {
      name: "tasksType",
      title: translate("taskTypes"),
      hideSort: true,
      hideSearch: true,
      formatComponent: renderTasksTypesName,
    },
    {
      name: "appEvents",
      title: translate("triggers"),
      hideSort: true,
      hideSearch: true,
      formatComponent: renderAppEventsName,
    },
    { name: "type", title: translate("type") },
    {
      name: "branches",
      title: translate("branch"),
      hideSearch: true,
      formatComponent: (branches?: Branch[]) => getBranchesReferences(branches),
    },
    {
      name: "status",
      hideSearch: true,
      title: translate("status"),
      formatComponent: (status: string) => (
        <BadgeStatus value={status} className={classes.status} />
      ),
    },
  ];

  const generalActionsTable: GeneralActions = {
    orderBy: {
      left: ["handleSearch", "handleFilterBy"],
      right: ["handleChangeStatus", "newRecord"],
    },
    handleSearch: {
      onSearch: handleSearch,
    },
    handleFilterBy: {
      categories: [
        {
          name: translate("status"),
          column: "status",
          options: [Status.ACTIVE, Status.INACTIVE],
          optionsSelected: handleGetDefaultFilters(),
        },
        {
          name: translate("description"),
          column: "description",
        },
        { name: translate("code"), column: "code" },
        { name: translate("type"), column: "type" },
      ],
      onChange: handleFilter,
    },
    newRecord:
      permission && isShowAction(currentUser, permission.create) ? (
        <Button
          customColor={Color.SECONDARY_DARK}
          startIcon={<Add />}
          onClick={() => {
            if (hasErrors !== undefined) setHasErrors(undefined);
            setSelectedChecklist(undefined);
            setIsView(false);
            setOpenModal(true);
          }}
        >
          <span className={classes.buttonText}>{translate("newChecklist")}</span>
        </Button>
      ) : undefined,
    handleChangeStatus:
      permission && isShowAction(currentUser, permission.changeStatus)
        ? renderButtonToChangeStatus()
        : undefined,
  };

  useEffect(() => {
    if (hasErrors !== undefined && !hasErrors) {
      NotificationToast.success(translate("operationPerformedSuccessfully"), {
        id: "request.success.message.notifications",
      });
      setHasErrors(undefined);
      setClearSelection(false);
      setRows([]);
    }
  }, [hasErrors]);

  return (
    <div data-cy="fleetListTable">
      {!openModal && (
        <ShipeeziNewGrid
          data={checklists}
          columns={tableColumns}
          totalItems={total}
          loadingData={isLoading}
          emptyData={translate("noInformationToBeShown")}
          pageSize={pageSize}
          pageSizeList={[20, 40, 60, 100]}
          currentPage={page}
          onChangePageSize={setPageSize}
          onNextPage={setCurrentPage}
          onPreviousPage={setCurrentPage}
          onGoToPage={setCurrentPage}
          rowActions={{
            orderBy: ["handleEditChecklist", "handleView", "handleChangeStatus"],
          }}
          rowActionsExtra={[
            {
              title: translate("edit"),
              key: "handleEditChecklist",
              buttonAndAction: editChecklistButtonAndAction,
            },
            { title: translate("view"), key: "handleView", buttonAndAction: viewChecklist },
            {
              title: translate("changeStatus"),
              key: "handleChangeStatus",
              buttonAndAction: changeStatusChecklistButtonAndAction,
            },
          ]}
          positionActionsColumn="right"
          onSorting={handleSort}
          clearSelection={clearSelection}
          handleSelect={(rowsArr: number[]) => handleSaveChecklistSelected(rowsArr)}
          generalActions={generalActionsTable}
        />
      )}

      {openModal && (
        <ChecklistProvider>
          <ChecklistModal
            checklistToUpdate={selectedChecklist}
            open={openModal}
            hasError={hasErrors}
            isOnViewMode={isView}
            isLoading={isLoading}
            closeModal={() => setOpenModal(false)}
            submit={(checklist?: ChecklistProps) => {
              if (checklist) {
                const checklistQuestionsOrdered = checklist?.questions?.map((question, index) => ({
                  ...question,
                  questionOrder: index + 1,
                }));

                checklist.questions = checklistQuestionsOrdered || [];

                if (checklist.id) {
                  updateChecklist({ id: checklist.id, checklist });
                } else {
                  createChecklist({
                    ...checklist,
                    companyId: currentUser?.companyId,
                  });
                }
              }
            }}
          />
        </ChecklistProvider>
      )}

      {openConfirmation && (
        <ConfirmChangeStatus
          open={openConfirmation}
          rows={rows}
          hasErrors={hasErrors}
          isLoading={isLoading}
          closeModal={() => setOpenConfirmation(false)}
          update={(rowId, status) => {
            if (rowId) {
              updateChecklist({
                id: rowId,
                checklist: { status } as ChecklistProps,
              });
              setClearSelection(true);
            }
          }}
        />
      )}
    </div>
  );
};

export default Checklist;
